import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { Navigate, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { Button, cn, Spinner } from '@c2fo/liquidity';
import NavigationBar from '@/enterprise/components/OfferBuilder/NavigationBar';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import useCreateMonduQuote, { MonduQuote } from '@/enterprise/data/useCreateMonduQuote';
import useMonduHandoffSkip from '@/enterprise/data/useMonduHandoffSkip';
import { BusinessDetailsStep } from './BusinessDetailsStep';
import { QUOTE_REQUEST_ERROR_CONTENT, WIZARD_CONTENT } from './content';
import { GetAQuoteModal } from './GetAQuoteModal';
import { ManualCustomersStep } from './ManualCustomersStep';
import { QuoteLoadingModal } from './QuoteLoadingModal';
import { STEP_ORDER, resolveStep } from './quoteWizardRouting';
import {
  AddedCustomer,
  BusinessDetailsDraft,
  QuoteWizardStep,
  buildCreateQuoteBody,
  customerKey,
  initialWizardState,
} from './quoteWizardState';

type StepStatus = 'complete' | 'current' | 'upcoming';

/**
 * One value, so consent and loading cannot be open together and a failure cannot show in two places.
 * `loading` is set before the mutation, not from `isPending`, so the consent modal closes and the
 * loading modal opens in the same render.
 */
type QuoteRequestPhase = 'idle' | 'consent' | 'loading' | 'failed';

const circleClassByStatus: Record<StepStatus, string> = {
  complete: 'border-2 border-primary-500 bg-white text-primary-500',
  current: 'bg-primary-500 text-white',
  upcoming: 'border-2 border-stroke bg-gray-100 text-text-secondary',
};

const Stepper = ({ currentStepIndex }: { currentStepIndex: number }) => (
  <ol className="flex w-full items-start justify-center">
    {WIZARD_CONTENT.steps.map((stepInfo, idx) => {
      const status: StepStatus =
        idx < currentStepIndex ? 'complete' : idx === currentStepIndex ? 'current' : 'upcoming';

      return (
        <Fragment key={stepInfo.index}>
          {idx > 0 && (
            <li
              aria-hidden="true"
              className={cn(
                'mx-1 mt-5 h-0.5 w-4 flex-none sm:mx-3 sm:mt-6 sm:min-w-8 sm:max-w-36 sm:flex-1',
                idx <= currentStepIndex ? 'bg-primary-500' : 'bg-stroke'
              )}
            />
          )}
          {/* Narrow screens: the steps flex to share the row and the labels wrap inside them. A fixed
              width here would let a nowrap label overflow the viewport. */}
          <li
            aria-current={status === 'current' ? 'step' : undefined}
            data-status={status}
            className="flex min-w-0 flex-1 flex-col items-center gap-2.5 sm:w-12 sm:flex-none"
          >
            <span
              className={cn(
                'flex size-10 items-center justify-center rounded-full text-lg sm:size-12 sm:text-xl',
                circleClassByStatus[status]
              )}
            >
              {stepInfo.index}
            </span>
            <span className="text-center text-sm text-text-primary sm:whitespace-nowrap sm:text-base">
              {stepInfo.label}
            </span>
          </li>
        </Fragment>
      );
    })}
  </ol>
);

export const GetAQuoteWizard = () => {
  const { step: stepSegment } = useParams();
  const navigate = useNavigate();
  const [businessDetails, setBusinessDetails] = useState<BusinessDetailsDraft | null>(
    initialWizardState.businessDetails
  );
  const [customers, setCustomers] = useState<AddedCustomer[]>(initialWizardState.customers);
  const [quote, setQuote] = useState<MonduQuote | null>(initialWizardState.quote);
  const [isBusinessDetailsValid, setIsBusinessDetailsValid] = useState(false);
  const [quoteRequestPhase, setQuoteRequestPhase] = useState<QuoteRequestPhase>('idle');
  const { canSkipModal } = useMonduHandoffSkip();
  const { mutateAsync: createQuote } = useCreateMonduQuote();
  // Filled by the step while it is mounted. See `BusinessDetailsStepProps.submitRef`.
  const submitBusinessDetails = useRef<(() => void) | null>(null);
  const [hasOpenCustomerEntry, setHasOpenCustomerEntry] = useState(false);
  // Set once "Next: Get Quote" has confirmed, so the navigation to Review Quote that follows the POST
  // is not asked about a second time.
  const isLeaveConfirmed = useRef(false);

  const blocker = useBlocker(() => hasOpenCustomerEntry && !isLeaveConfirmed.current);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    if (window.confirm(WIZARD_CONTENT.unsavedCustomerPrompt)) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  useEffect(() => {
    if (!hasOpenCustomerEntry) {
      isLeaveConfirmed.current = false;
      return undefined;
    }

    // Browsers show their own leave-page dialog here and ignore any message.
    const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasOpenCustomerEntry]);

  // The address is the only source of the current step; there is no copy of it in state to disagree.
  const { step, shouldRedirect } = resolveStep(stepSegment, {
    hasBusinessDetails: businessDetails !== null,
    hasQuote: quote !== null,
  });
  const currentStepIndex = STEP_ORDER.indexOf(step);

  // A failure describes the entries the supplier just submitted. It says nothing once they leave.
  useEffect(() => {
    if (step === 'add-customers') return;

    setQuoteRequestPhase((current) => (current === 'failed' ? 'idle' : current));
  }, [step]);

  const canGoBack = currentStepIndex > 0;

  /**
   * Forward actions push and backward actions pop, so history always mirrors the path the supplier
   * actually walked. `relative: 'path'` keeps these resolving against the address rather than the route
   * tree, which is what makes a sibling step the obvious destination.
   */
  const goToStep = (target: QuoteWizardStep) => navigate(`../${target}`, { relative: 'path' });
  const goBack = () => navigate(-1);

  const handleBack = () => {
    if (canGoBack) {
      goBack();
    }
  };

  const handleBusinessDetailsSubmit = (draft: BusinessDetailsDraft) => {
    setBusinessDetails(draft);
    void goToStep('add-customers');
  };

  // The step blocks a duplicate name before calling this, so appending cannot collide.
  const handleAddCustomer = (customer: AddedCustomer) => setCustomers((current) => [...current, customer]);

  const handleUpdateCustomer = (originalKey: string, customer: AddedCustomer) =>
    setCustomers((current) => {
      const index = current.findIndex((existing) => customerKey(existing.name) === originalKey);
      if (index < 0) return current;
      const next = current.slice();
      next[index] = customer;
      return next;
    });

  const handleRemoveCustomer = (key: string) =>
    setCustomers((current) => current.filter((customer) => customerKey(customer.name) !== key));

  /** Creates the quote and advances on success. Every failure is the same inline error. */
  const requestQuote = async (draft: BusinessDetailsDraft): Promise<void> => {
    // Set before the request, so a retry clears the inline error in the same render that opens loading.
    setQuoteRequestPhase('loading');

    try {
      const createdQuote = await createQuote(buildCreateQuoteBody(draft, customers));

      // A quote without a status of `quoted`, or without a range to show, has nothing for Review to
      // render. SEA distinguishes `supplier_not_identified` from `provider_failed`; telling them
      // apart in the UI needs copy that does not exist yet, so both read as one failure here.
      if (createdQuote.status !== 'quoted' || !createdQuote.supplierAprRange) {
        isLeaveConfirmed.current = false;
        setQuoteRequestPhase('failed');
        return;
      }

      setQuote(createdQuote);
      setQuoteRequestPhase('idle');
      void goToStep('review-quote');
    } catch {
      // The hook already reported it to DataDog; the wizard only needs to show the failure.
      isLeaveConfirmed.current = false;
      setQuoteRequestPhase('failed');
    }
  };

  /**
   * A supplier who already acknowledged the current Mondu consent and already has a Mondu supplier
   * has nothing left to disclose, so Generate Quote posts directly. Everyone else — including every
   * supplier whose consent state is still loading or failed to load — gets the modal. Same rule as
   * `ExtendedNetworkFinance`.
   */
  const handleGenerateQuote = async (): Promise<void> => {
    if (!businessDetails) {
      return;
    }

    // Asked here rather than by the blocker: the blocker only sees the navigation after the POST, by
    // which point the quote has already been requested.
    if (hasOpenCustomerEntry) {
      if (!window.confirm(WIZARD_CONTENT.unsavedCustomerPrompt)) return;
      isLeaveConfirmed.current = true;
    }

    if (!canSkipModal) {
      setQuoteRequestPhase('consent');
      return;
    }

    await requestQuote(businessDetails);
  };

  const closeConsentModal = (): void => {
    // The consent dialog can still report a dismiss while it is closing into the loading modal.
    // Honoring that would cancel a request that has already started.
    setQuoteRequestPhase((current) => {
      if (current !== 'consent') return current;
      isLeaveConfirmed.current = false;
      return 'idle';
    });
  };

  /**
   * Review Quote has no footer. Its actions live on the page, and this one returns to Add Customers —
   * so it pops like every other backward move. Pushing here would leave the footer Back on Add
   * Customers popping to Review Quote instead of Business Details.
   */
  const handleModifyCustomerSelection = () => goBack();

  // Checked after the hooks so the step bodies below never render against a draft they need and lack.
  // Replacing rather than pushing: a push would put the unreachable address back in history, and the
  // next back press would re-trigger this redirect and pin the supplier here.
  if (shouldRedirect) {
    return <Navigate to={`../${step}`} relative="path" replace />;
  }

  return (
    <div className="mx-auto max-w-full px-4 py-6 md:max-w-7xl">
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center space-y-2 text-center text-text-primary">
          <div className="text-4xl font-bold">{WIZARD_CONTENT.title}</div>
          <div className="text-base">
            <span className="font-bold">{WIZARD_CONTENT.subtitleEmphasis}</span>
            {WIZARD_CONTENT.subtitle}
          </div>
        </div>

        <Stepper currentStepIndex={currentStepIndex} />
      </div>

      <div className="mt-8">
        {step === 'business-details' && (
          // The step prefills its contact fields from the logged-in user, so it suspends.
          <Suspense
            fallback={
              <div className="flex h-64 w-full items-center justify-center">
                <Spinner className="h-12 w-12 fill-primary-500" />
              </div>
            }
          >
            <BusinessDetailsStep
              draft={businessDetails}
              onSubmit={handleBusinessDetailsSubmit}
              onValidityChange={setIsBusinessDetailsValid}
              submitRef={submitBusinessDetails}
            />
          </Suspense>
        )}
        {step === 'add-customers' && (
          <ManualCustomersStep
            customers={customers}
            onAdd={handleAddCustomer}
            onUpdate={handleUpdateCustomer}
            onRemove={handleRemoveCustomer}
            onOpenEntryChange={setHasOpenCustomerEntry}
            quoteError={quoteRequestPhase === 'failed' ? QUOTE_REQUEST_ERROR_CONTENT : undefined}
          />
        )}
        {step === 'review-quote' && (
          // `data-quote-id` stands in until Review Quote renders the stored quote for real.
          <div
            data-testid="review-placeholder"
            data-quote-id={quote?.quoteId}
            className="flex flex-col items-start gap-4"
          >
            <div>Review Quote placeholder (slice 5)</div>
            <Button variant="tertiary" onClick={handleModifyCustomerSelection}>
              {WIZARD_CONTENT.actions.modifyCustomerSelection}
            </Button>
          </div>
        )}
      </div>

      {step !== 'review-quote' && (
        <PageBottomPortal>
          <NavigationBar>
            {/* Lets a test scope the actions and the failure to the footer rather than the step body. */}
            <div
              data-testid="wizard-footer"
              className="flex w-full shrink-0 flex-col items-center gap-4 md:ml-auto md:w-fit md:flex-row"
            >
              <Button className="w-full md:w-fit" variant="secondary" onClick={handleBack} disabled={!canGoBack}>
                {WIZARD_CONTENT.actions.back}
              </Button>
              {step === 'business-details' ? (
                // The footer is portalled away from the step body, so Next calls the step's submit
                // handler directly rather than relying on a `form` association.
                <Button
                  className="w-full md:w-fit"
                  onClick={() => submitBusinessDetails.current?.()}
                  disabled={!isBusinessDetailsValid}
                >
                  {WIZARD_CONTENT.actions.next}
                </Button>
              ) : (
                // Disabled without a draft or a saved customer. Step 1 supplies the quotes body, and a
                // quote is not offered until at least one customer has been saved.
                <Button
                  className="w-full md:w-fit"
                  variant="primary"
                  onClick={() => void handleGenerateQuote()}
                  disabled={!businessDetails || customers.length === 0 || quoteRequestPhase === 'loading'}
                >
                  {WIZARD_CONTENT.actions.generateQuote}
                </Button>
              )}
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}

      <GetAQuoteModal
        open={quoteRequestPhase === 'consent'}
        onClose={closeConsentModal}
        onAcknowledge={() => {
          if (businessDetails) {
            void requestQuote(businessDetails);
          }
        }}
      />
      <QuoteLoadingModal open={quoteRequestPhase === 'loading'} />
    </div>
  );
};
